import React, { useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, query, orderBy, where, getDocs, doc, setDoc, updateDoc, deleteDoc, serverTimestamp, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../firebaseConfig';
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  TransitionChild,
} from '@headlessui/react';
import {
  Bars3Icon,
  XMarkIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  UserIcon,
  PhoneIcon,
  EnvelopeIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  ShieldExclamationIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  InformationCircleIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';
import { GiDominoMask, GiRadarSweep, GiSoundWaves, GiSpeedometer, GiEchoRipples } from "react-icons/gi";
import { MdContacts } from "react-icons/md";
import { useLocation } from 'react-router-dom';

// Function to get navigation items
function getFilteredNavigation(pathname) {
  return [
    { name: 'Home', href: '/overview', icon: GiSpeedometer, current: pathname === '/overview' },
    { name: 'Relay', href: '/dashboard', icon: GiDominoMask, current: pathname === '/dashboard' },
    { name: 'Messages', href: '/dashboard/trace', icon: GiEchoRipples, current: pathname === '/dashboard/trace' },
    { name: 'World Call', href: '/world-call', icon: GiSoundWaves, current: pathname === '/world-call' },
    { name: 'Contacts', href: '/contacts', icon: MdContacts, current: pathname === '/contacts' },
    { name: 'Monitor', href: '/dashboard/monitor', icon: GiRadarSweep, current: pathname === '/dashboard/monitor' },
  ];
}

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function ContactsList() {
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [user, setUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name'); // 'name', 'date', 'threat'
  const [showAddContact, setShowAddContact] = useState(false);
  const [showThreatInfo, setShowThreatInfo] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const [selectedContactThreatExpanded, setSelectedContactThreatExpanded] = useState(false);
  
  const [newContact, setNewContact] = useState({
    phoneNumber: '',
    firstName: '',
    lastName: '',
    company: '',
    otherPhones: [''],
    emails: [''],
    notes: ''
  });

  const location = useLocation();
  const navigation = getFilteredNavigation(location.pathname);
  const settingsNavItem = { name: 'Settings', href: '/settings', icon: Cog6ToothIcon, current: location.pathname === '/settings' };

  // Check for mobile view
  useEffect(() => {
    const checkMobileView = () => {
      setIsMobileView(window.innerWidth < 1024);
    };
    
    checkMobileView();
    window.addEventListener('resize', checkMobileView);
    
    return () => {
      window.removeEventListener('resize', checkMobileView);
    };
  }, []);

  // Load user and contacts
  useEffect(() => {
    let contactsUnsubscribe = null;
    
    const authUnsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        contactsUnsubscribe = await loadContacts(currentUser.uid);
      } else {
        setUser(null);
        setContacts([]);
        setSelectedContact(null);
        setLoading(false);
        if (contactsUnsubscribe) {
          contactsUnsubscribe();
          contactsUnsubscribe = null;
        }
      }
    });

    return () => {
      authUnsubscribe();
      if (contactsUnsubscribe) {
        contactsUnsubscribe();
      }
    };
  }, []);

  // Check for phone number search from VoiceDialer navigation
  useEffect(() => {
    const contactSearchNumber = localStorage.getItem('contactSearchNumber');
    if (contactSearchNumber) {
      setSearchTerm(contactSearchNumber);
      localStorage.removeItem('contactSearchNumber'); // Clean up after use
    }
  }, []);

  // Load contacts
  const loadContacts = async (uid) => {
    try {
      const contactsRef = collection(db, 'userContacts', uid, 'contacts');
      const contactsQuery = query(contactsRef, orderBy('createdAt', 'desc'));
      
      // Use onSnapshot for real-time updates
      const unsubscribe = onSnapshot(contactsQuery, (snapshot) => {
        const contactsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setContacts(contactsData);
        setLoading(false);
      });

      return unsubscribe;
    } catch (error) {
      console.error('Error loading contacts:', error);
      setLoading(false);
    }
  };

  // Contact management functions
  const handleUpdateContact = async (updatedContactData) => {
    if (!user || !selectedContact) return;

    try {
      const contactRef = doc(db, 'userContacts', user.uid, 'contacts', selectedContact.id);
      await updateDoc(contactRef, {
        ...updatedContactData,
        updatedAt: serverTimestamp()
      });

      // Update local state
      setContacts(prev => prev.map(contact => 
        contact.id === selectedContact.id ? { ...contact, ...updatedContactData } : contact
      ));
      setSelectedContact(prev => ({ ...prev, ...updatedContactData }));
    } catch (error) {
      console.error('Error updating contact:', error);
    }
  };

  const handleDeleteContact = async () => {
    if (!user || !selectedContact) return;

    try {
      const contactRef = doc(db, 'userContacts', user.uid, 'contacts', selectedContact.id);
      await deleteDoc(contactRef);

      // Update local state
      setContacts(prev => prev.filter(contact => contact.id !== selectedContact.id));
      setSelectedContact(null);
    } catch (error) {
      console.error('Error deleting contact:', error);
    }
  };

  const handleAddContact = async () => {
    if (!user || !newContact.phoneNumber.trim()) return;

    try {
      // Normalize phone number for ID
      const normalizedPhone = normalizePhoneNumber(newContact.phoneNumber);
      const contactRef = doc(db, 'userContacts', user.uid, 'contacts', normalizedPhone);

      const contactData = {
        phoneNumber: normalizedPhone,
        displayNumber: newContact.phoneNumber,
        firstName: newContact.firstName.trim(),
        lastName: newContact.lastName.trim(),
        company: newContact.company.trim(),
        otherPhones: newContact.otherPhones.filter(phone => phone.trim()),
        emails: newContact.emails.filter(email => email.trim()),
        notes: newContact.notes.trim(),
        sources: {},
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isManual: true,
        threatAssessment: null
      };

      await setDoc(contactRef, contactData);

      // Reset form
      setNewContact({
        phoneNumber: '',
        firstName: '',
        lastName: '',
        company: '',
        otherPhones: [''],
        emails: [''],
        notes: ''
      });
      setShowAddContact(false);
    } catch (error) {
      console.error('Error adding contact:', error);
    }
  };

  // Helper functions - updated to support international numbers
  const normalizePhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';
    
    // If already in E.164 format, return as is
    if (phoneNumber.startsWith('+') && phoneNumber.replace(/\D/g, '').length >= 7) {
      return phoneNumber;
    }
    
    // Remove all non-digit characters
    const digits = phoneNumber.replace(/\D/g, '');
    
    // If it's incomplete, return as is
    if (digits.length < 7) {
      return phoneNumber;
    }
    
    // Handle US/Canada numbers (11 digits starting with 1, or 10 digits)
    if (digits.length === 11 && digits.startsWith('1')) {
      return `+${digits}`;
    } else if (digits.length === 10) {
      // Assume US number if no country code
      return `+1${digits}`;
    } else {
      // For international numbers, just add + if missing
      return `+${digits}`;
    }
  };

  const filteredAndSortedContacts = () => {
    let filtered = contacts;

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = contacts.filter(contact => 
        contact.firstName?.toLowerCase().includes(term) ||
        contact.lastName?.toLowerCase().includes(term) ||
        contact.company?.toLowerCase().includes(term) ||
        contact.phoneNumber?.includes(term) ||
        contact.displayNumber?.includes(term) ||
        contact.notes?.toLowerCase().includes(term) ||
        contact.threatAssessment?.carrier?.name?.toLowerCase().includes(term) ||
        contact.threatAssessment?.callerName?.caller_name?.toLowerCase().includes(term)
      );
    }

    // Sort
    return filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          const nameA = `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.phoneNumber;
          const nameB = `${b.firstName || ''} ${b.lastName || ''}`.trim() || b.phoneNumber;
          return nameA.localeCompare(nameB);
        case 'date':
          const dateA = a.lastContactDate?.toDate() || a.createdAt?.toDate() || new Date(0);
          const dateB = b.lastContactDate?.toDate() || b.createdAt?.toDate() || new Date(0);
          return dateB - dateA;
        case 'threat':
          const levelA = getThreatLevel(a.threatAssessment);
          const levelB = getThreatLevel(b.threatAssessment);
          const threatOrder = { 'High': 3, 'Medium': 2, 'Low': 1, 'Unknown': 0 };
          return (threatOrder[levelB] || 0) - (threatOrder[levelA] || 0);
        default:
          return 0;
      }
    });
  };

  const getThreatLevel = (threatAssessment) => {
    if (!threatAssessment || !threatAssessment.hasData) return 'Unknown';
    
    const { carrier, portability, fraud } = threatAssessment;
    
    // High risk factors
    if (fraud?.score > 0.7) return 'High';
    if (carrier?.type === 'voip' && portability?.ported_status === 'Y') return 'High';
    if (carrier?.type === 'mobile' && !carrier?.mobile_country_code) return 'High';
    
    // Medium risk factors
    if (carrier?.type === 'voip') return 'Medium';
    if (portability?.ported_status === 'Y') return 'Medium';
    if (fraud?.score > 0.3) return 'Medium';
    
    return 'Low';
  };

  const getThreatLevelColor = (level) => {
    switch (level) {
      case 'High': return 'text-red-400 bg-red-500/20';
      case 'Medium': return 'text-yellow-400 bg-yellow-500/20';
      case 'Low': return 'text-green-400 bg-green-500/20';
      default: return 'text-gray-400 bg-gray-500/20';
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    
    const date = timestamp.toDate ? timestamp.toDate() : timestamp;
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  };

  const getThreatAnalysis = (threatAssessment) => {
    if (!threatAssessment || !threatAssessment.hasData) {
      return "No threat assessment data available for this contact.";
    }

    return "This number appears safe and has a low fraud risk score.";
  };

  return (
    <>
      <div className="flex min-h-screen text-white" 
           style={{background: 'linear-gradient(to bottom right, #0d0520, #1a0b2e, #0a0615, #050208)'}}>
        
        {/* Mobile sidebar */}
        <Dialog open={sidebarOpen} onClose={setSidebarOpen} className="relative z-50 lg:hidden">
          <DialogBackdrop transition className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-[closed]:opacity-0" />
          <div className="fixed inset-0 flex">
            <DialogPanel transition className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-[closed]:-translate-x-full">
              <TransitionChild>
                <div className="absolute left-full top-0 flex w-16 justify-center pt-5 duration-300 ease-in-out data-[closed]:opacity-0">
                  <button type="button" onClick={() => setSidebarOpen(false)} className="-m-2.5 p-2.5">
                    <span className="sr-only">Close sidebar</span>
                    <XMarkIcon aria-hidden="true" className="h-6 w-6 text-white" />
                  </button>
                </div>
              </TransitionChild>
              
              <div 
                className="flex grow flex-col gap-y-5 overflow-y-auto m-4 rounded-2xl border border-white/10 shadow-2xl"
                style={{
                  background: 'rgba(0, 0, 0, 0.2)',
                  backdropFilter: 'blur(20px)',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.4)'
                }}
              >
                <div className="flex h-16 shrink-0 items-center justify-center px-4">
                  <img
                    alt="Relay"
                    src="/images/logowhite.png"
                    className="h-8 w-auto"
                  />
                </div>
                <nav className="flex flex-1 flex-col px-4">
                  <ul role="list" className="flex flex-1 flex-col gap-y-7">
                    <li>
                      <ul role="list" className="space-y-3">
                        {navigation.map((item) => (
                          <li key={item.name}>
                            <a
                              href={item.href}
                              className="group flex items-center gap-x-3 p-3 text-sm font-medium transition-all duration-200"
                              onClick={() => setSidebarOpen(false)}
                            >
                              <item.icon 
                                aria-hidden="true" 
                                className={classNames(
                                  "h-6 w-6 shrink-0 transition-colors duration-200",
                                  item.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                                )}
                              />
                              <span className={classNames(
                                "transition-colors duration-200",
                                item.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                              )}>
                                {item.name}
                              </span>
                            </a>
                          </li>
                        ))}
                      </ul>
                    </li>
                    <li className="mt-auto mb-4">
                      <a
                        href={settingsNavItem.href}
                        className="group flex items-center gap-x-3 p-3 text-sm font-medium transition-all duration-200"
                        onClick={() => setSidebarOpen(false)}
                      >
                        <settingsNavItem.icon 
                          aria-hidden="true" 
                          className={classNames(
                            "h-6 w-6 shrink-0 transition-colors duration-200",
                            settingsNavItem.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                          )} 
                        />
                        <span className={classNames(
                          "transition-colors duration-200",
                          settingsNavItem.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                        )}>
                          {settingsNavItem.name}
                        </span>
                      </a>
                    </li>
                  </ul>
                </nav>
              </div>
            </DialogPanel>
          </div>
        </Dialog>

        {/* Desktop sidebar */}
        <div className={`hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:flex-col transition-all duration-300 ${
          sidebarExpanded ? 'lg:w-72' : 'lg:w-20'
        }`}>
          <div className="relative">
            <div 
              className="flex grow flex-col gap-y-5 overflow-y-auto m-4 rounded-2xl border border-white/10 shadow-2xl relative"
              style={{
                background: 'rgba(0, 0, 0, 0.2)',
                backdropFilter: 'blur(20px)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.4)',
                height: 'calc(100vh - 2rem)'
              }}
            >
              {/* Header with logo */}
              <div className={`flex h-16 shrink-0 items-center justify-center transition-all duration-300 ${sidebarExpanded ? 'px-4' : 'px-1'}`}>
                <img
                  alt="Relay"
                  src="/images/logowhite.png"
                  className={`transition-all duration-300 ${sidebarExpanded ? 'h-8 w-auto' : 'h-10 w-auto max-w-none'}`}
                  style={{ filter: 'drop-shadow(0 0 8px rgba(255,255,255,0.1))' }}
                />
              </div>

              <nav className={`flex flex-1 flex-col ${sidebarExpanded ? 'px-4' : 'px-1'}`}>
                <ul role="list" className="flex flex-1 flex-col gap-y-7">
                  <li>
                    <ul role="list" className="space-y-3">
                      {navigation.map((item) => (
                        <li key={item.name}>
                          <a
                            href={item.href}
                            className={classNames(
                              'group flex items-center text-sm font-medium transition-all duration-200 rounded-lg',
                              sidebarExpanded ? 'gap-x-3 p-3' : 'justify-center p-2'
                            )}
                            title={!sidebarExpanded ? item.name : undefined}
                          >
                            <item.icon 
                              aria-hidden="true" 
                              className={classNames(
                                "h-6 w-6 shrink-0 transition-colors duration-200",
                                item.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                              )} 
                            />
                            {sidebarExpanded && (
                              <span className={classNames(
                                "transition-opacity duration-300",
                                item.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                              )}>
                                {item.name}
                              </span>
                            )}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </li>
                  <li className="mt-auto mb-4">
                    <a
                      href={settingsNavItem.href}
                      className={classNames(
                        'group flex items-center text-sm font-medium transition-all duration-200 rounded-lg',
                        sidebarExpanded ? 'gap-x-3 p-3' : 'justify-center p-2'
                      )}
                      title={!sidebarExpanded ? settingsNavItem.name : undefined}
                    >
                      <settingsNavItem.icon 
                        aria-hidden="true" 
                        className={classNames(
                          "h-6 w-6 shrink-0 transition-colors duration-200",
                          settingsNavItem.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                        )} 
                      />
                      {sidebarExpanded && (
                        <span className={classNames(
                          "transition-opacity duration-300",
                          settingsNavItem.current ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                        )}>
                          {settingsNavItem.name}
                        </span>
                      )}
                    </a>
                  </li>
                </ul>
              </nav>
            </div>

            {/* Right border expand/collapse area */}
            <div 
              className="absolute top-0 right-0 w-4 h-full cursor-pointer group flex items-center justify-center"
              onClick={() => setSidebarExpanded(!sidebarExpanded)}
              style={{ zIndex: 60 }}
            >
              {/* Hover indicator */}
              <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white/10 backdrop-blur-md rounded-full p-1 border border-white/20">
                {sidebarExpanded ? (
                  <ChevronLeftIcon className="h-4 w-4 text-white/70" />
                ) : (
                  <ChevronRightIcon className="h-4 w-4 text-white/70" />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className={`flex-1 transition-all duration-300 ${
          sidebarExpanded ? 'lg:pl-80' : 'lg:pl-28'
        }`}>
          <div className="p-4 lg:p-6">
            {/* Mobile Menu Button */}
            <div className="flex items-center justify-between mb-6">
              <button type="button" onClick={() => setSidebarOpen(true)} className="lg:hidden bg-black/20 backdrop-blur-2xl rounded-2xl border border-white/10 p-3 text-white hover:bg-black/30 transition-all"
                     style={{boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 20px rgba(0,0,0,0.2)'}}>
                <span className="sr-only">Open sidebar</span>
                <Bars3Icon aria-hidden="true" className="h-6 w-6" />
              </button>
            </div>

            {/* Desktop Layout */}
            {!isMobileView ? (
              <div className="flex h-screen">
                {/* Contacts List Panel */}
                <div className="w-1/3 flex flex-col">
                  {/* Header - Liquid Glass Design */}
                  <div className="flex-shrink-0 p-4 sticky top-0 z-10">
                    <div 
                      className="bg-white/5 backdrop-blur-2xl rounded-2xl border border-white/10 p-6"
                      style={{
                        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.4)'
                      }}
                    >
                      <div className="mb-6">
                        <h1 className="text-2xl font-semibold text-white">Contacts</h1>
                      </div>
                      
                      {/* Search */}
                      <div className="relative mb-4">
                        <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-white/40" />
                        <input
                          type="text"
                          placeholder="Search contacts..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full pl-10 pr-4 py-3 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200"
                        />
                      </div>
                      
                      {/* Sort and Add Row */}
                      <div className="flex gap-3">
                        <Menu as="div" className="relative flex-1">
                          <MenuButton className="flex items-center justify-between gap-2 px-4 py-3 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl text-white/80 hover:bg-white/10 transition-all duration-200 w-full">
                            <span>Sort: {sortBy === 'name' ? 'Name' : sortBy === 'date' ? 'Recent' : 'Threat Level'}</span>
                            <ChevronDownIcon className="h-4 w-4" />
                          </MenuButton>
                          <MenuItems className="absolute z-20 mt-2 w-full bg-black/90 backdrop-blur-2xl border border-white/10 rounded-xl shadow-2xl overflow-hidden">
                            <MenuItem>
                              <button
                                onClick={() => setSortBy('name')}
                                className="block w-full px-4 py-3 text-left text-white hover:bg-white/10 first:rounded-t-xl transition-all duration-200"
                              >
                                Name
                              </button>
                            </MenuItem>
                            <MenuItem>
                              <button
                                onClick={() => setSortBy('date')}
                                className="block w-full px-4 py-3 text-left text-white hover:bg-white/10 transition-all duration-200"
                              >
                                Recent Contact
                              </button>
                            </MenuItem>
                            <MenuItem>
                              <button
                                onClick={() => setSortBy('threat')}
                                className="block w-full px-4 py-3 text-left text-white hover:bg-white/10 last:rounded-b-xl transition-all duration-200"
                              >
                                Threat Level
                              </button>
                            </MenuItem>
                          </MenuItems>
                        </Menu>
                        
                        <button
                          onClick={() => setShowAddContact(true)}
                          className="flex items-center gap-2 px-4 py-3 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl text-white/80 hover:bg-white/10 transition-all duration-200"
                        >
                          <PlusIcon className="h-4 w-4" />
                          Add
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Contacts List */}
                  <div className="flex-1 overflow-y-auto p-4">
                    {loading ? (
                      <div className="text-center text-white/60 py-8">Loading contacts...</div>
                    ) : filteredAndSortedContacts().length === 0 ? (
                      <div className="text-center text-white/60 py-8">
                        {searchTerm ? 'No contacts found matching your search.' : 'No contacts yet. Add your first contact!'}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {filteredAndSortedContacts().map((contact) => (
                          <div
                            key={contact.id}
                            onClick={() => setSelectedContact(contact)}
                            className={classNames(
                              "p-4 rounded-xl border cursor-pointer transition-all duration-200",
                              selectedContact?.id === contact.id
                                ? "bg-indigo-600/20 border-indigo-500/50"
                                : "bg-white/5 border-white/10 hover:bg-white/10"
                            )}
                            style={{
                              backdropFilter: 'blur(20px)',
                            }}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 bg-indigo-600/30 rounded-full flex items-center justify-center">
                                    <UserIcon className="h-5 w-5 text-indigo-300" />
                                  </div>
                                  <div>
                                    <p className="text-white font-medium truncate">
                                      {`${contact.firstName || ''} ${contact.lastName || ''}`.trim() || contact.phoneNumber}
                                    </p>
                                    <p className="text-white/60 text-sm truncate">{contact.phoneNumber}</p>
                                    {contact.company && (
                                      <p className="text-white/40 text-xs truncate">{contact.company}</p>
                                    )}
                                  </div>
                                </div>
                              </div>
                              
                              {/* Threat indicator */}
                              {contact.threatAssessment?.hasData && (
                                <div className={classNames(
                                  "w-3 h-3 rounded-full border-2 border-current",
                                  getThreatLevelColor(getThreatLevel(contact.threatAssessment))
                                )} />
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Contact Details */}
                <div className="flex-1 flex flex-col relative">
                  {selectedContact ? (
                    <>
                      {/* Contact Details Panel - Full Height */}
                      <div className="flex-1 p-6 overflow-y-auto">
                        <div 
                          className="bg-white/5 backdrop-blur-2xl rounded-2xl border border-white/10 p-6"
                          style={{
                            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.4)'
                          }}
                        >
                          <h2 className="text-xl font-semibold text-white mb-6">Contact Details</h2>
                          
                          <div className="space-y-6">
                            {/* Name Section */}
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-white/80 mb-2">
                                  <UserIcon className="h-4 w-4 inline mr-2" />
                                  First Name
                                </label>
                                <input
                                  type="text"
                                  value={selectedContact.firstName || ''}
                                  onChange={(e) => handleUpdateContact({ firstName: e.target.value })}
                                  className="w-full px-4 py-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200"
                                  placeholder="First name"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-white/80 mb-2">
                                  Last Name
                                </label>
                                <input
                                  type="text"
                                  value={selectedContact.lastName || ''}
                                  onChange={(e) => handleUpdateContact({ lastName: e.target.value })}
                                  className="w-full px-4 py-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200"
                                  placeholder="Last name"
                                />
                              </div>
                            </div>

                            {/* Phone Number */}
                            <div>
                              <label className="block text-sm font-medium text-white/80 mb-2">
                                <PhoneIcon className="h-4 w-4 inline mr-2" />
                                Primary Phone
                              </label>
                              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl px-4 py-2">
                                <span className="text-white">{selectedContact.phoneNumber}</span>
                                <span className="text-white/40 text-sm ml-2">(Primary - Cannot be changed)</span>
                              </div>
                            </div>

                            {/* Company */}
                            <div>
                              <label className="block text-sm font-medium text-white/80 mb-2">
                                <BuildingOfficeIcon className="h-4 w-4 inline mr-2" />
                                Company
                              </label>
                              <input
                                type="text"
                                value={selectedContact.company || ''}
                                onChange={(e) => handleUpdateContact({ company: e.target.value })}
                                className="w-full px-4 py-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200"
                                placeholder="Company name"
                              />
                            </div>

                            {/* Other Phone Numbers */}
                            <div>
                              <label className="block text-sm font-medium text-white/80 mb-2">
                                Other Phone Numbers
                              </label>
                              <div className="space-y-2">
                                {(selectedContact.otherPhones || []).map((phone, index) => (
                                  <div key={index} className="flex gap-2">
                                    <input
                                      type="tel"
                                      value={phone}
                                      onChange={(e) => {
                                        const newPhones = [...(selectedContact.otherPhones || [])];
                                        newPhones[index] = e.target.value;
                                        handleUpdateContact({ otherPhones: newPhones });
                                      }}
                                      className="flex-1 px-4 py-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200"
                                      placeholder="Additional phone number"
                                    />
                                    <button
                                      onClick={() => {
                                        const newPhones = (selectedContact.otherPhones || []).filter((_, i) => i !== index);
                                        handleUpdateContact({ otherPhones: newPhones });
                                      }}
                                      className="px-3 py-2 bg-red-500/20 border border-red-500/30 rounded-xl text-red-400 hover:bg-red-500/30 transition-all duration-200"
                                    >
                                      <XMarkIcon className="h-4 w-4" />
                                    </button>
                                  </div>
                                ))}
                                <button
                                  onClick={() => {
                                    const newPhones = [...(selectedContact.otherPhones || []), ''];
                                    handleUpdateContact({ otherPhones: newPhones });
                                  }}
                                  className="w-full px-4 py-2 bg-white/5 backdrop-blur-sm border border-white/10 border-dashed rounded-xl text-white/60 hover:bg-white/10 hover:text-white transition-all duration-200"
                                >
                                  <PlusIcon className="h-4 w-4 inline mr-2" />
                                  Add Phone Number
                                </button>
                              </div>
                            </div>

                            {/* Email Addresses */}
                            <div>
                              <label className="block text-sm font-medium text-white/80 mb-2">
                                <EnvelopeIcon className="h-4 w-4 inline mr-2" />
                                Email Addresses
                              </label>
                              <div className="space-y-2">
                                {(selectedContact.emails || []).map((email, index) => (
                                  <div key={index} className="flex gap-2">
                                    <input
                                      type="email"
                                      value={email}
                                      onChange={(e) => {
                                        const newEmails = [...(selectedContact.emails || [])];
                                        newEmails[index] = e.target.value;
                                        handleUpdateContact({ emails: newEmails });
                                      }}
                                      className="flex-1 px-4 py-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200"
                                      placeholder="email@example.com"
                                    />
                                    <button
                                      onClick={() => {
                                        const newEmails = (selectedContact.emails || []).filter((_, i) => i !== index);
                                        handleUpdateContact({ emails: newEmails });
                                      }}
                                      className="px-3 py-2 bg-red-500/20 border border-red-500/30 rounded-xl text-red-400 hover:bg-red-500/30 transition-all duration-200"
                                    >
                                      <XMarkIcon className="h-4 w-4" />
                                    </button>
                                  </div>
                                ))}
                                <button
                                  onClick={() => {
                                    const newEmails = [...(selectedContact.emails || []), ''];
                                    handleUpdateContact({ emails: newEmails });
                                  }}
                                  className="w-full px-4 py-2 bg-white/5 backdrop-blur-sm border border-white/10 border-dashed rounded-xl text-white/60 hover:bg-white/10 hover:text-white transition-all duration-200"
                                >
                                  <PlusIcon className="h-4 w-4 inline mr-2" />
                                  Add Email Address
                                </button>
                              </div>
                            </div>

                            {/* Notes */}
                            <div>
                              <label className="block text-sm font-medium text-white/80 mb-2">
                                <DocumentTextIcon className="h-4 w-4 inline mr-2" />
                                Notes
                              </label>
                              <textarea
                                value={selectedContact.notes || ''}
                                onChange={(e) => handleUpdateContact({ notes: e.target.value })}
                                rows={4}
                                className="w-full px-4 py-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200 resize-none"
                                placeholder="Notes about this contact..."
                              />
                            </div>

                            {/* Contact Sources */}
                            <div>
                              <label className="block text-sm font-medium text-white/80 mb-2">
                                Contact Sources
                              </label>
                              <div className="flex flex-wrap gap-2">
                                {Object.keys(selectedContact.sources || {}).map((source) => (
                                  <span
                                    key={source}
                                    className="px-3 py-1 bg-indigo-600/20 border border-indigo-500/30 rounded-full text-indigo-300 text-sm"
                                  >
                                    {source === 'messaging' ? 'Messages' : 
                                     source === 'voice' ? 'Voice Calls' : 
                                     source === 'manual' ? 'Manual' : source}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>


                    </>
                  ) : (
                    <div className="flex-1 flex items-center justify-center">
                      <div className="text-center text-white/40">
                        <UserIcon className="h-16 w-16 mx-auto mb-4 text-white/20" />
                        <p className="text-lg">Select a contact to view details</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* Mobile Layout */
              <div className="h-screen flex flex-col">
                {!selectedContact ? (
                  /* Mobile Contacts List */
                  <div className="flex-1 flex flex-col">
                    {/* Mobile Header - Liquid Glass Design */}
                    <div className="flex-shrink-0 p-4 sticky top-16 z-20">
                      <div 
                        className="bg-white/5 backdrop-blur-2xl rounded-2xl border border-white/10 p-4"
                        style={{
                          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.4)'
                        }}
                      >
                        <div className="mb-4">
                          <h1 className="text-xl font-semibold text-white">Contacts</h1>
                        </div>
                        
                        {/* Mobile Search */}
                        <div className="relative mb-3">
                          <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-white/40" />
                          <input
                            type="text"
                            placeholder="Search contacts..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-3 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200"
                          />
                        </div>
                        
                        {/* Mobile Sort and Add */}
                        <div className="flex gap-3">
                          <Menu as="div" className="relative flex-1">
                            <MenuButton className="flex items-center justify-between gap-2 px-4 py-3 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl text-white/80 hover:bg-white/10 transition-all duration-200 w-full">
                              <span>Sort: {sortBy === 'name' ? 'Name' : sortBy === 'date' ? 'Recent' : 'Threat Level'}</span>
                              <ChevronDownIcon className="h-4 w-4" />
                            </MenuButton>
                            <MenuItems className="absolute z-30 mt-2 w-full bg-black/90 backdrop-blur-2xl border border-white/10 rounded-xl shadow-2xl overflow-hidden">
                              <MenuItem>
                                <button
                                  onClick={() => setSortBy('name')}
                                  className="block w-full px-4 py-3 text-left text-white hover:bg-white/10 first:rounded-t-xl transition-all duration-200"
                                >
                                  Name
                                </button>
                              </MenuItem>
                              <MenuItem>
                                <button
                                  onClick={() => setSortBy('date')}
                                  className="block w-full px-4 py-3 text-left text-white hover:bg-white/10 transition-all duration-200"
                                >
                                  Recent Contact
                                </button>
                              </MenuItem>
                              <MenuItem>
                                <button
                                  onClick={() => setSortBy('threat')}
                                  className="block w-full px-4 py-3 text-left text-white hover:bg-white/10 last:rounded-b-xl transition-all duration-200"
                                >
                                  Threat Level
                                </button>
                              </MenuItem>
                            </MenuItems>
                          </Menu>
                          
                          <button
                            onClick={() => setShowAddContact(true)}
                            className="flex items-center gap-2 px-4 py-3 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl text-white/80 hover:bg-white/10 transition-all duration-200"
                          >
                            <PlusIcon className="h-4 w-4" />
                            Add
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Mobile Contacts List */}
                    <div className="flex-1 overflow-y-auto p-4">
                      {loading ? (
                        <div className="text-center text-white/60 py-8">Loading contacts...</div>
                      ) : filteredAndSortedContacts().length === 0 ? (
                        <div className="text-center text-white/60 py-8">
                          {searchTerm ? 'No contacts found matching your search.' : 'No contacts yet. Add your first contact!'}
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {filteredAndSortedContacts().map((contact) => (
                            <div
                              key={contact.id}
                              onClick={() => setSelectedContact(contact)}
                              className="p-4 rounded-xl border bg-white/5 border-white/10 hover:bg-white/10 cursor-pointer transition-all duration-200"
                              style={{
                                backdropFilter: 'blur(20px)',
                              }}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                  <div className="w-12 h-12 bg-indigo-600/30 rounded-full flex items-center justify-center">
                                    <UserIcon className="h-6 w-6 text-indigo-300" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-white font-medium truncate">
                                      {`${contact.firstName || ''} ${contact.lastName || ''}`.trim() || contact.phoneNumber}
                                    </p>
                                    <p className="text-white/60 text-sm truncate">{contact.phoneNumber}</p>
                                    {contact.company && (
                                      <p className="text-white/40 text-xs truncate">{contact.company}</p>
                                    )}
                                  </div>
                                </div>
                                
                                <div className="flex items-center gap-2">
                                  {/* Threat indicator */}
                                  {contact.threatAssessment?.hasData && (
                                    <div className={classNames(
                                      "w-3 h-3 rounded-full border-2 border-current",
                                      getThreatLevelColor(getThreatLevel(contact.threatAssessment))
                                    )} />
                                  )}
                                  <ChevronRightIcon className="h-5 w-5 text-white/40" />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  /* Mobile Contact Details */
                  <div className="flex-1 flex flex-col">
                    {/* Mobile Detail Header */}
                    <div 
                      className="flex-shrink-0 px-4 py-4 border-b border-white/10"
                      style={{
                        background: 'rgba(0, 0, 0, 0.2)',
                        backdropFilter: 'blur(20px)',
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setSelectedContact(null)}
                          className="w-8 h-8 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 transition-all"
                        >
                          <ChevronLeftIcon className="h-4 w-4" />
                        </button>
                        <div className="flex-1">
                          <h1 className="text-lg font-semibold text-white">
                            {`${selectedContact.firstName || ''} ${selectedContact.lastName || ''}`.trim() || selectedContact.phoneNumber}
                          </h1>
                          <p className="text-white/60 text-sm">{selectedContact.phoneNumber}</p>
                        </div>
                      </div>
                    </div>

                    {/* Mobile Contact Details Content */}
                    <div className="flex-1 overflow-y-auto p-4 pb-32">
                      <div 
                        className="bg-white/5 backdrop-blur-2xl rounded-2xl border border-white/10 p-4"
                        style={{
                          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 32px rgba(0,0,0,0.4)'
                        }}
                      >
                        {/* Same contact details form as desktop but optimized for mobile */}
                        <div className="space-y-4">
                          {/* Name fields in mobile stack vertically */}
                          <div className="space-y-3">
                            <div>
                              <label className="block text-sm font-medium text-white/80 mb-2">
                                <UserIcon className="h-4 w-4 inline mr-2" />
                                First Name
                              </label>
                              <input
                                type="text"
                                value={selectedContact.firstName || ''}
                                onChange={(e) => handleUpdateContact({ firstName: e.target.value })}
                                className="w-full px-4 py-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200"
                                placeholder="First name"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-white/80 mb-2">
                                Last Name
                              </label>
                              <input
                                type="text"
                                value={selectedContact.lastName || ''}
                                onChange={(e) => handleUpdateContact({ lastName: e.target.value })}
                                className="w-full px-4 py-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200"
                                placeholder="Last name"
                              />
                            </div>
                          </div>

                          {/* Company */}
                          <div>
                            <label className="block text-sm font-medium text-white/80 mb-2">
                              <BuildingOfficeIcon className="h-4 w-4 inline mr-2" />
                              Company
                            </label>
                            <input
                              type="text"
                              value={selectedContact.company || ''}
                              onChange={(e) => handleUpdateContact({ company: e.target.value })}
                              className="w-full px-4 py-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200"
                              placeholder="Company name"
                            />
                          </div>

                          {/* Notes */}
                          <div>
                            <label className="block text-sm font-medium text-white/80 mb-2">
                              <DocumentTextIcon className="h-4 w-4 inline mr-2" />
                              Notes
                            </label>
                            <textarea
                              value={selectedContact.notes || ''}
                              onChange={(e) => handleUpdateContact({ notes: e.target.value })}
                              rows={3}
                              className="w-full px-4 py-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200 resize-none"
                              placeholder="Notes about this contact..."
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Mobile Sticky Threat Assessment */}
                    {selectedContact.threatAssessment && (
                      <div className={`fixed bottom-0 left-0 right-0 z-40 transition-all duration-300 ${
                        selectedContactThreatExpanded ? 'h-96' : 'h-auto'
                      }`}>
                        <div 
                          className="bg-red-500/10 backdrop-blur-2xl border-t border-red-500/30 p-4 h-full overflow-y-auto"
                          style={{
                            boxShadow: 'inset 0 1px 0 rgba(255,100,100,0.1), 0 -8px 32px rgba(220,38,38,0.2)'
                          }}
                        >
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-6 h-6 bg-red-500/20 rounded-lg flex items-center justify-center">
                              <GiRadarSweep className="h-4 w-4 text-red-400" />
                            </div>
                            <h3 className="text-sm font-semibold text-white">Threat Assessment</h3>
                            <button
                              onClick={() => setShowThreatInfo(true)}
                              className="w-4 h-4 bg-white/10 rounded-full flex items-center justify-center text-white/60 hover:text-white hover:bg-white/20 transition-all duration-200"
                            >
                              <InformationCircleIcon className="h-3 w-3" />
                            </button>
                          </div>
                          
                          {selectedContact.threatAssessment?.hasData ? (
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <div className={classNames(
                                  "px-2 py-1 rounded-full text-xs font-medium border",
                                  getThreatLevelColor(getThreatLevel(selectedContact.threatAssessment))
                                )}>
                                  {getThreatLevel(selectedContact.threatAssessment).toUpperCase()} RISK
                                </div>
                                {selectedContact.threatAssessment.carrier?.name && (
                                  <span className="text-white/60 text-xs">
                                    {selectedContact.threatAssessment.carrier.name}
                                  </span>
                                )}
                              </div>
                              <button
                                onClick={() => setSelectedContactThreatExpanded(!selectedContactThreatExpanded)}
                                className="flex items-center gap-1 text-white/60 hover:text-white transition-all duration-200"
                              >
                                {selectedContactThreatExpanded ? (
                                  <ChevronUpIcon className="h-3 w-3" />
                                ) : (
                                  <ChevronDownIcon className="h-3 w-3" />
                                )}
                                <span className="text-xs">See details</span>
                              </button>
                              
                              {/* Expanded Mobile Content */}
                              {selectedContactThreatExpanded && (
                                <div className="mt-4 space-y-3">
                                  {/* Quick Summary */}
                                  <div className="bg-white/5 backdrop-blur-sm rounded-xl p-3 border border-white/10">
                                    <div className="space-y-2 text-xs">
                                      {selectedContact.threatAssessment.carrier?.name && (
                                        <div>
                                          <span className="text-white/60">Carrier:</span>
                                          <p className="text-white font-medium">{selectedContact.threatAssessment.carrier.name}</p>
                                        </div>
                                      )}
                                      {selectedContact.threatAssessment.portability?.line_type && (
                                        <div>
                                          <span className="text-white/60">Line Type:</span>
                                          <p className="text-white font-medium capitalize">{selectedContact.threatAssessment.portability.line_type}</p>
                                        </div>
                                      )}
                                      {selectedContact.threatAssessment.callerName?.caller_name && (
                                        <div>
                                          <span className="text-white/60">Caller ID:</span>
                                          <p className="text-white font-medium">{selectedContact.threatAssessment.callerName.caller_name}</p>
                                        </div>
                                      )}
                                      {selectedContact.threatAssessment.portability?.city && selectedContact.threatAssessment.portability?.state && (
                                        <div>
                                          <span className="text-white/60">Location:</span>
                                          <p className="text-white font-medium">
                                            {selectedContact.threatAssessment.portability.city}, {selectedContact.threatAssessment.portability.state}
                                          </p>
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  {/* AI Analysis */}
                                  <div className="bg-orange-500/5 backdrop-blur-sm rounded-xl p-3 border border-orange-500/20">
                                    <h4 className="text-orange-300 font-medium mb-2 text-xs">AI Analysis</h4>
                                    <p className="text-white/80 text-xs">
                                      {getThreatAnalysis(selectedContact.threatAssessment)}
                                    </p>
                                  </div>

                                  {/* Raw Data - Mobile */}
                                  <div className="bg-black/20 backdrop-blur-sm rounded-xl p-3 border border-white/10">
                                    <h4 className="text-white/80 font-medium mb-2 text-xs">Raw Threat Data</h4>
                                    <div className="space-y-2 text-xs">
                                      <div>
                                        <span className="text-white/40">Phone Number:</span>
                                        <p className="text-white/80 font-mono text-xs">{selectedContact.threatAssessment.phoneNumber}</p>
                                      </div>
                                      <div>
                                        <span className="text-white/40">Country Code:</span>
                                        <p className="text-white/80 font-mono text-xs">{selectedContact.threatAssessment.countryCode}</p>
                                      </div>
                                      <div>
                                        <span className="text-white/40">National Format:</span>
                                        <p className="text-white/80 font-mono text-xs">{selectedContact.threatAssessment.nationalFormat}</p>
                                      </div>
                                      {selectedContact.threatAssessment.portability?.lrn && (
                                        <div>
                                          <span className="text-white/40">LRN:</span>
                                          <p className="text-white/80 font-mono text-xs">{selectedContact.threatAssessment.portability.lrn}</p>
                                        </div>
                                      )}
                                      
                                      {/* Carrier Details */}
                                      {selectedContact.threatAssessment.carrier && (
                                        <div>
                                          <span className="text-white/40">Carrier Details:</span>
                                          <pre className="text-white/80 font-mono text-xs mt-1 bg-black/30 p-2 rounded overflow-x-auto max-h-16">
                                            {JSON.stringify(selectedContact.threatAssessment.carrier, null, 2)}
                                          </pre>
                                        </div>
                                      )}
                                      
                                      {/* Portability Details */}
                                      {selectedContact.threatAssessment.portability && (
                                        <div>
                                          <span className="text-white/40">Portability Details:</span>
                                          <pre className="text-white/80 font-mono text-xs mt-1 bg-black/30 p-2 rounded overflow-x-auto max-h-16">
                                            {JSON.stringify(selectedContact.threatAssessment.portability, null, 2)}
                                          </pre>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white/60"></div>
                              <span className="text-white/60 text-xs">Analyzing...</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Full Screen Bottom Threat Assessment (Desktop & Mobile) */}
        {selectedContact?.threatAssessment && !isMobileView && (
          <div className={`fixed bottom-0 left-0 right-0 z-50 transition-all duration-300 ${
            selectedContactThreatExpanded ? 'h-96' : 'h-auto'
          }`}>
            <div 
              className="bg-red-500/10 backdrop-blur-2xl border-t border-red-500/30 p-6 h-full overflow-y-auto"
              style={{
                boxShadow: 'inset 0 1px 0 rgba(255,100,100,0.1), 0 -8px 32px rgba(220,38,38,0.2)'
              }}
            >
              <div className="max-w-7xl mx-auto">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-red-500/20 rounded-lg flex items-center justify-center">
                      <GiRadarSweep className="h-5 w-5 text-red-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-white">Threat Assessment</h3>
                    <button
                      onClick={() => setShowThreatInfo(true)}
                      className="w-5 h-5 bg-white/10 rounded-full flex items-center justify-center text-white/60 hover:text-white hover:bg-white/20 transition-all duration-200"
                    >
                      <InformationCircleIcon className="h-3 w-3" />
                    </button>
                  </div>
                  <button
                    onClick={() => setSelectedContactThreatExpanded(!selectedContactThreatExpanded)}
                    className="flex items-center gap-2 text-white/60 hover:text-white transition-all duration-200"
                  >
                    {selectedContactThreatExpanded ? (
                      <ChevronUpIcon className="h-4 w-4" />
                    ) : (
                      <ChevronDownIcon className="h-4 w-4" />
                    )}
                    <span className="text-sm">
                      {selectedContactThreatExpanded ? 'Minimize' : 'Expand'}
                    </span>
                  </button>
                </div>
                
                {selectedContact.threatAssessment?.hasData ? (
                  <div className="space-y-4">
                    {/* Always visible summary */}
                    <div className="flex items-center gap-3">
                      <div className={classNames(
                        "px-3 py-1 rounded-full text-sm font-medium border",
                        getThreatLevelColor(getThreatLevel(selectedContact.threatAssessment))
                      )}>
                        {getThreatLevel(selectedContact.threatAssessment).toUpperCase()} RISK
                      </div>
                      {selectedContact.threatAssessment.carrier?.name && (
                        <span className="text-white/60 text-sm">
                          {selectedContact.threatAssessment.carrier.name}
                        </span>
                      )}
                      {selectedContact.threatAssessment.portability?.line_type && (
                        <span className="text-white/60 text-sm">
                          • {selectedContact.threatAssessment.portability.line_type}
                        </span>
                      )}
                      {selectedContact.threatAssessment.callerName?.caller_name && (
                        <span className="text-white/60 text-sm">
                          • {selectedContact.threatAssessment.callerName.caller_name}
                        </span>
                      )}
                    </div>

                    {/* Expanded content */}
                    {selectedContactThreatExpanded && (
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Quick Summary */}
                        <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                          <h4 className="text-white/80 font-medium mb-3">Summary</h4>
                          <div className="space-y-2 text-sm">
                            {selectedContact.threatAssessment.carrier?.name && (
                              <div>
                                <span className="text-white/60">Carrier:</span>
                                <p className="text-white font-medium">{selectedContact.threatAssessment.carrier.name}</p>
                              </div>
                            )}
                            {selectedContact.threatAssessment.portability?.line_type && (
                              <div>
                                <span className="text-white/60">Line Type:</span>
                                <p className="text-white font-medium capitalize">{selectedContact.threatAssessment.portability.line_type}</p>
                              </div>
                            )}
                            {selectedContact.threatAssessment.callerName?.caller_name && (
                              <div>
                                <span className="text-white/60">Caller ID:</span>
                                <p className="text-white font-medium">{selectedContact.threatAssessment.callerName.caller_name}</p>
                              </div>
                            )}
                            {selectedContact.threatAssessment.portability?.city && selectedContact.threatAssessment.portability?.state && (
                              <div>
                                <span className="text-white/60">Location:</span>
                                <p className="text-white font-medium">
                                  {selectedContact.threatAssessment.portability.city}, {selectedContact.threatAssessment.portability.state}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* AI Analysis */}
                        <div className="bg-orange-500/5 backdrop-blur-sm rounded-xl p-4 border border-orange-500/20">
                          <h4 className="text-orange-300 font-medium mb-2">AI Analysis</h4>
                          <p className="text-white/80 text-sm">
                            {getThreatAnalysis(selectedContact.threatAssessment)}
                          </p>
                        </div>

                        {/* Raw Data */}
                        <div className="bg-black/20 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                          <h4 className="text-white/80 font-medium mb-3">Raw Threat Data</h4>
                          <div className="space-y-3 text-xs">
                            <div>
                              <span className="text-white/40">Phone Number:</span>
                              <p className="text-white/80 font-mono">{selectedContact.threatAssessment.phoneNumber}</p>
                            </div>
                            <div>
                              <span className="text-white/40">Country Code:</span>
                              <p className="text-white/80 font-mono">{selectedContact.threatAssessment.countryCode}</p>
                            </div>
                            <div>
                              <span className="text-white/40">National Format:</span>
                              <p className="text-white/80 font-mono">{selectedContact.threatAssessment.nationalFormat}</p>
                            </div>
                            {selectedContact.threatAssessment.portability?.lrn && (
                              <div>
                                <span className="text-white/40">LRN:</span>
                                <p className="text-white/80 font-mono">{selectedContact.threatAssessment.portability.lrn}</p>
                              </div>
                            )}
                            
                            {/* Carrier Details */}
                            {selectedContact.threatAssessment.carrier && (
                              <div>
                                <span className="text-white/40">Carrier Details:</span>
                                <pre className="text-white/80 font-mono text-xs mt-1 bg-black/30 p-2 rounded overflow-x-auto max-h-20">
                                  {JSON.stringify(selectedContact.threatAssessment.carrier, null, 2)}
                                </pre>
                              </div>
                            )}
                            
                            {/* Portability Details */}
                            {selectedContact.threatAssessment.portability && (
                              <div>
                                <span className="text-white/40">Portability Details:</span>
                                <pre className="text-white/80 font-mono text-xs mt-1 bg-black/30 p-2 rounded overflow-x-auto max-h-20">
                                  {JSON.stringify(selectedContact.threatAssessment.portability, null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : selectedContact.threatAssessment?.error ? (
                  <div className="flex items-center gap-3">
                    <ExclamationTriangleIcon className="h-6 w-6 text-yellow-400" />
                    <div>
                      <p className="text-white/60">Threat assessment failed</p>
                      <p className="text-white/40 text-sm">{selectedContact.threatAssessment.error}</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white/60"></div>
                    <div>
                      <p className="text-white/60">Analyzing threat level...</p>
                      <p className="text-white/40 text-sm">This may take a few moments</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add Contact Modal */}
      <Dialog open={showAddContact} onClose={setShowAddContact} className="relative z-50">
        <DialogBackdrop className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <DialogPanel className="mx-auto max-w-md w-full">
              <div 
                className="relative bg-black/20 backdrop-blur-2xl rounded-3xl border border-white/10 p-6"
                style={{
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 20px 40px rgba(0,0,0,0.3)'
                }}
              >
                <button
                  onClick={() => setShowAddContact(false)}
                  className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/10 backdrop-blur border border-white/20 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 transition-all"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>

                <h2 className="text-xl font-medium text-white mb-6">Add New Contact</h2>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      value={newContact.phoneNumber}
                      onChange={(e) => setNewContact({...newContact, phoneNumber: e.target.value})}
                      className="w-full px-4 py-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-2">
                        First Name
                      </label>
                      <input
                        type="text"
                        value={newContact.firstName}
                        onChange={(e) => setNewContact({...newContact, firstName: e.target.value})}
                        className="w-full px-4 py-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200"
                        placeholder="John"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-2">
                        Last Name
                      </label>
                      <input
                        type="text"
                        value={newContact.lastName}
                        onChange={(e) => setNewContact({...newContact, lastName: e.target.value})}
                        className="w-full px-4 py-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200"
                        placeholder="Doe"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">
                      Company
                    </label>
                    <input
                      type="text"
                      value={newContact.company}
                      onChange={(e) => setNewContact({...newContact, company: e.target.value})}
                      className="w-full px-4 py-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200"
                      placeholder="Acme Corp"
                    />
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => setShowAddContact(false)}
                    className="flex-1 px-4 py-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl text-white/80 hover:bg-white/10 transition-all duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddContact}
                    className="flex-1 px-4 py-2 bg-indigo-600/80 backdrop-blur-sm border border-indigo-500/30 rounded-xl text-white hover:bg-indigo-600 transition-all duration-200"
                  >
                    Add Contact
                  </button>
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>

      {/* Threat Assessment Info Modal */}
      <Dialog open={showThreatInfo} onClose={setShowThreatInfo} className="relative z-50">
        <DialogBackdrop className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <DialogPanel className="mx-auto max-w-md w-full">
              <div 
                className="relative bg-black/20 backdrop-blur-2xl rounded-3xl border border-white/10 p-6"
                style={{
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 20px 40px rgba(0,0,0,0.3)'
                }}
              >
                <button
                  onClick={() => setShowThreatInfo(false)}
                  className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/10 backdrop-blur border border-white/20 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 transition-all"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>

                <h2 className="text-xl font-medium text-white mb-4">About Threat Assessment</h2>
                
                <div className="text-white/80 space-y-3 text-sm">
                  <p>
                    We assess threat based on signals we find online and in telecommunication networks with the help of AI.
                  </p>
                  <p>
                    <strong>AI can make mistakes.</strong> This information is provided for reference only and should not be the sole basis for important decisions.
                  </p>
                  <p>
                    Data sources include carrier information, line type analysis, caller ID databases, and fraud detection signals.
                  </p>
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </>
  );
} 